import React, { useContext, useState } from 'react'
import PropTypes from 'prop-types'
import { useParams } from 'react-router-dom'
import Input from 'components/Input'
import Textarea from 'components/Textarea'
import { delay } from 'utils/delay'
import { requestUpdateSiteSpecieAssesmentParameter } from 'api/siteSpecieAssesmentParameter'
import { isValidDecimalNumber, noZeroAfterDecimal } from 'utils/isValidDecimalNumber'
import { SiteContext } from 'providers/site'
import { SITE_STATUS_DRAFT_ID } from 'constants/siteStatuses'
import { minBestMaxValidation } from 'utils/minBestMaxValidation'
import toNumberOrNull from 'utils/toNumberOrNull'
import { SpeciesDetailsContainer, SpeciesDetailsItemWrapper } from '../style'
import { NUMBER_OF_MATURE_INDIVIDUALS_ID, NUMBER_OF_LOCALITIES_ID } from 'constants/speciesParameterType'

const fields = {
  min: 'regionalMin',
  best: 'regionalBest',
  max: 'regionalMax'
}

const inputSaveDelayTime = 300
const updateInputTimeOut = delay(inputSaveDelayTime)

function parseRegionalValue (rawValue, parameterTypeId, keepDecimalsForMatureBest = false) {
  const isMatureOrLocalities = (
    parameterTypeId === NUMBER_OF_MATURE_INDIVIDUALS_ID ||
    parameterTypeId === NUMBER_OF_LOCALITIES_ID
  )
  const decimals = isMatureOrLocalities && !keepDecimalsForMatureBest ? 0 : 10
  return Number(Number.parseFloat(rawValue).toFixed(decimals))
}

const RegionalNationalEstimatesDataContent = ({
  parameter,
  siteSpecieParameterId,
  updateParameterList,
  species,
  qualified
}) => {
  const params = useParams()
  const siteHistoryIdParam = toNumberOrNull(params.siteHistoryId)
  const { site, isEditable } = useContext(SiteContext)

  const disabled = !parameter?.SiteAssesmentParameterTypeId || site.SiteStatusId !== SITE_STATUS_DRAFT_ID || !isEditable || !qualified

  const [regionalMin, setRegionalMin] = useState(parameter.minRegional ? parameter.minRegional : '')
  const [regionalBest, setRegionalBest] = useState(parameter.bestRegional ? parameter.bestRegional : '')
  const [regionalMax, setRegionalMax] = useState(parameter.maxRegional ? parameter.maxRegional : '')
  const [regionalEstimate, setRegionalEstimate] = useState(parameter.sourceRegionalData ? parameter.sourceRegionalData : '')
  const [euPopulation, setEuPopulation] = useState(parameter.euPopulation ? parameter.euPopulation : '')
  const [nationalPopulation, setNationalPopulation] = useState(parameter.nationalPopulation ? parameter.nationalPopulation : '')
  const [euEstimate, setEuEstimate] = useState(parameter.sourceOfEu ? parameter.sourceOfEu : '')
  const [nationalEstimate, setNationalEstimate] = useState(parameter.sourceOfNational ? parameter.sourceOfNational : '')
  const [error, setError] = useState({ field: '', error: '' })

  function handleChangeRegionalMin(e) {
    const errorMessage = minBestMaxValidation(e.target.value, regionalBest, regionalMax, fields, 'min')
    if (errorMessage.field !== '') {
      if (isValidDecimalNumber(Number(e.target.value))) {
        if (noZeroAfterDecimal(e.target.value)) {
          setRegionalMin(e.target.value)
          setError(errorMessage)
        }
      }
      return
    }
    if (isValidDecimalNumber(Number(e.target.value))) {
      if (noZeroAfterDecimal(e.target.value)) {
        setRegionalMin(e.target.value)
        setError(errorMessage)
      }
      const parsedValue = parseRegionalValue(e.target.value, parameter.SiteAssesmentParameterTypeId)
      requestUpdateSiteSpecieAssesmentParameter(siteSpecieParameterId, siteHistoryIdParam, species.id, species.historyId, {
        minRegional: e.target.value === '' ? null : parsedValue
      }).then(
        (data) => {
          updateParameterList(data)
        }
      )
    }
  }
  function handleBlurRegionalMin(e) {
    if (((parameter.SiteAssesmentParameterTypeId === NUMBER_OF_MATURE_INDIVIDUALS_ID) || (parameter.SiteAssesmentParameterTypeId === NUMBER_OF_LOCALITIES_ID)) && e.target.value.includes('.')) {
      return
    }

    const errorMessage = minBestMaxValidation(e.target.value, regionalBest, regionalMax, fields, 'min')
    if (errorMessage.field !== '') {
      if (isValidDecimalNumber(Number(e.target.value))) {
        if (noZeroAfterDecimal(e.target.value)) {
          setRegionalMin(e.target.value)
          setError(errorMessage)
        }
      }
      return
    }
    if (isValidDecimalNumber(Number(e.target.value))) {
      if (noZeroAfterDecimal(e.target.value)) {
        setRegionalMin(e.target.value)
        setError(errorMessage)
      }

      requestUpdateSiteSpecieAssesmentParameter(siteSpecieParameterId, siteHistoryIdParam, species.id, species.historyId, {
        minRegional: e.target.value === '' ? null : e.target.value
      }).then(
        (data) => {
          updateParameterList(data)
        }
      )
    }
  }

  function handleChangeRegionalBest(e) {
    const errorMessage = minBestMaxValidation(regionalMin, e.target.value, regionalMax, fields, 'best')
    if (errorMessage.field !== '') {
      if (isValidDecimalNumber(Number(e.target.value))) {
        if (noZeroAfterDecimal(e.target.value)) {
          setRegionalBest(e.target.value)
          setError(errorMessage)
        }
      }
      return
    }
    if (isValidDecimalNumber(Number(e.target.value))) {
      if (noZeroAfterDecimal(e.target.value)) {
        setRegionalBest(e.target.value)
        setError(errorMessage)
      }
      const parsedValue = parseRegionalValue(
        e.target.value,
        parameter.SiteAssesmentParameterTypeId
      )
      requestUpdateSiteSpecieAssesmentParameter(siteSpecieParameterId, siteHistoryIdParam, species.id, species.historyId, {
        bestRegional: e.target.value === '' ? null : parsedValue
      }).then(
        (data) => {
          updateParameterList(data)
        }
      )
    }
  }
  function handleBlurRegionalBest(e) {
    if (((parameter.SiteAssesmentParameterTypeId === NUMBER_OF_MATURE_INDIVIDUALS_ID) || (parameter.SiteAssesmentParameterTypeId === NUMBER_OF_LOCALITIES_ID)) && e.target.value.includes('.')) {
      return
    }

    const errorMessage = minBestMaxValidation(regionalMin, e.target.value, regionalMax, fields, 'best')
    if (errorMessage.field !== '') {
      if (isValidDecimalNumber(Number(e.target.value))) {
        if (noZeroAfterDecimal(e.target.value)) {
          setRegionalBest(e.target.value)
          setError(errorMessage)
        }
      }
      return
    }
    if (isValidDecimalNumber(Number(e.target.value))) {
      if (noZeroAfterDecimal(e.target.value)) {
        setRegionalBest(e.target.value)
        setError(errorMessage)
      }

      requestUpdateSiteSpecieAssesmentParameter(siteSpecieParameterId, siteHistoryIdParam, species.id, species.historyId, {
        bestRegional: e.target.value === '' ? null : e.target.value
      }).then(
        (data) => {
          updateParameterList(data)
        }
      )
    }
  }

  function handleChangeRegionalMax(e) {
    const errorMessage = minBestMaxValidation(regionalMin, regionalBest, e.target.value, fields, 'max')
    if (errorMessage.field !== '') {
      if (isValidDecimalNumber(Number(e.target.value))) {
        if (noZeroAfterDecimal(e.target.value)) {
          setRegionalMax(e.target.value)
          setError(errorMessage)
        }
      }
      return
    }
    if (isValidDecimalNumber(Number(e.target.value))) {
      if (noZeroAfterDecimal(e.target.value)) {
        setRegionalMax(e.target.value)
        setError(errorMessage)
      }
      const parsedValue = parseRegionalValue(e.target.value, parameter.SiteAssesmentParameterTypeId)
      requestUpdateSiteSpecieAssesmentParameter(siteSpecieParameterId, siteHistoryIdParam, species.id, species.historyId, {
        maxRegional: e.target.value === '' ? null : parsedValue
      }).then(
        (data) => {
          updateParameterList(data)
        }
      )
    }
  }
  function handleBlurRegionalMax(e) {
    if (((parameter.SiteAssesmentParameterTypeId === NUMBER_OF_MATURE_INDIVIDUALS_ID) || (parameter.SiteAssesmentParameterTypeId === NUMBER_OF_LOCALITIES_ID)) && e.target.value.includes('.')) {
      return
    }

    const errorMessage = minBestMaxValidation(regionalMin, regionalBest, e.target.value, fields, 'max')
    if (errorMessage.field !== '') {
      if (isValidDecimalNumber(Number(e.target.value))) {
        if (noZeroAfterDecimal(e.target.value)) {
          setRegionalMax(e.target.value)
          setError(errorMessage)
        }
      }
      return
    }
    if (isValidDecimalNumber(Number(e.target.value))) {
      if (noZeroAfterDecimal(e.target.value)) {
        setRegionalMax(e.target.value)
        setError(errorMessage)
      }

      requestUpdateSiteSpecieAssesmentParameter(siteSpecieParameterId, siteHistoryIdParam, species.id, species.historyId, {
        maxRegional: e.target.value === '' ? null : e.target.value
      }).then(
        (data) => {
          updateParameterList(data)
        }
      )
    }
  }

  function handleChangeRegionalEstimate(e) {
    setRegionalEstimate(e.target.value)

    updateInputTimeOut(() => {
      requestUpdateSiteSpecieAssesmentParameter(siteSpecieParameterId, siteHistoryIdParam, species.id, species.historyId, { sourceRegionalData: e.target.value }).then(
        (data) => {
          updateParameterList(data)
        }
      )
    })
  }

  function handleChangeEuPopulation(e) {
    if (isValidDecimalNumber(Number(e.target.value))) {
      if (noZeroAfterDecimal(e.target.value)) {
        setEuPopulation(e.target.value)
      }

      updateInputTimeOut(() => {
        requestUpdateSiteSpecieAssesmentParameter(siteSpecieParameterId, siteHistoryIdParam, species.id, species.historyId, { euPopulation: e.target.value }).then(
          (data) => {
            updateParameterList(data)
          }
        )
      })
    }
  }

  function handleChangeEuEstimate(e) {
    setEuEstimate(e.target.value)

    updateInputTimeOut(() => {
      requestUpdateSiteSpecieAssesmentParameter(siteSpecieParameterId, siteHistoryIdParam, species.id, species.historyId, { sourceOfEu: e.target.value }).then((data) => {
        updateParameterList(data)
      })
    })
  }

  function handleChangeNationalPopulation(e) {
    if (isValidDecimalNumber(Number(e.target.value))) {
      if (noZeroAfterDecimal(e.target.value)) {
        setNationalPopulation(e.target.value)
      }

      updateInputTimeOut(() => {
        requestUpdateSiteSpecieAssesmentParameter(siteSpecieParameterId, siteHistoryIdParam, species.id, species.historyId, { nationalPopulation: e.target.value }).then(
          (data) => {
            updateParameterList(data)
          }
        )
      })
    }
  }

  function handleChangeNationalEstimate(e) {
    setNationalEstimate(e.target.value)

    updateInputTimeOut(() => {
      requestUpdateSiteSpecieAssesmentParameter(siteSpecieParameterId, siteHistoryIdParam, species.id, species.historyId, { sourceOfNational: e.target.value }).then((data) => {
        updateParameterList(data)
      })
    })
  }

  return (
    <SpeciesDetailsContainer noPadding={true}>
      <SpeciesDetailsItemWrapper>
        <Input
          value={regionalMin}
          onChange={handleChangeRegionalMin}
          onBlur={handleBlurRegionalMin}
          placeholder='enterRegionalMin'
          label='regionalMin'
          field='regionalMin'
          infoTooltip='regionalMinHint'
          zIndex='1070'
          disabled={disabled}
          isOptional={true}
          error={error.field === 'regionalMin' ? error : { field: '', error: '' }}
          errorMarginTop='8px'
        />
      </SpeciesDetailsItemWrapper>
      <SpeciesDetailsItemWrapper>
        <Input
          value={regionalBest}
          onChange={handleChangeRegionalBest}
          onBlur={handleBlurRegionalBest}
          placeholder='enterRegionalBest'
          label='regionalBest'
          field='regionalBest'
          infoTooltip='regionalBestHint'
          zIndex='1070'
          disabled={disabled}
          errorMarginTop='8px'
          error={error.field === 'regionalBest' ? error : { field: '', error: '' }}
          isOptional={true}
        />
      </SpeciesDetailsItemWrapper>
      <SpeciesDetailsItemWrapper>
        <Input
          value={regionalMax}
          onChange={handleChangeRegionalMax}
          onBlur={handleBlurRegionalMax}
          placeholder='enterRegionalMax'
          label='regionalMax'
          field='regionalMax'
          infoTooltip='regionalMaxHint'
          zIndex='1070'
          disabled={disabled}
          isOptional={true}
          errorMarginTop='8px'
          error={error.field === 'regionalMax' ? error : { field: '', error: '' }}
        />
      </SpeciesDetailsItemWrapper>
      <SpeciesDetailsItemWrapper>
        <Input
          value={regionalEstimate}
          onChange={handleChangeRegionalEstimate}
          placeholder='enterRegionalEstimate'
          label='regionalEstimate'
          field='regionalEstimate'
          infoTooltip='regionalEstimateHint'
          zIndex='1070'
          disabled={disabled}
          errorMarginTop='8px'
          isOptional={true}
        />
      </SpeciesDetailsItemWrapper>

      <SpeciesDetailsItemWrapper>
        <Input
          value={euPopulation}
          onChange={handleChangeEuPopulation}
          placeholder='enterEuPopulation'
          label='euPopulation'
          field='euPopulation'
          infoTooltip='euPopulationHint'
          zIndex='1070'
          disabled={disabled}
          isOptional={true}
          errorMarginTop='8px'
        />
      </SpeciesDetailsItemWrapper>
      <SpeciesDetailsItemWrapper>
        <Input
          value={nationalPopulation}
          onChange={handleChangeNationalPopulation}
          placeholder='enterNationalPopulation'
          label='nationalPopulation'
          field='nationalPopulation'
          infoTooltip='nationalPopulationHint'
          zIndex='1070'
          disabled={disabled}
          errorMarginTop='8px'
          isOptional={true}
        />
      </SpeciesDetailsItemWrapper>
      <div style={{ width: '100%' }}>
        <Textarea
          value={euEstimate}
          onChange={handleChangeEuEstimate}
          placeholder='enterEuEstimate'
          label='euEstimate'
          field='euEstimate'
          infoTooltip='euEstimateHint'
          zIndex='1070'
          height='128px'
          disabled={disabled}
          isOptional={true}
        />
      </div>
      <div style={{ width: '100%' }}>
        <Textarea
          value={nationalEstimate}
          onChange={handleChangeNationalEstimate}
          placeholder='enterNationalEstimate'
          label='nationalEstimate'
          field='nationalEstimate'
          infoTooltip='nationalEstimateHint'
          zIndex='1070'
          height='128px'
          disabled={disabled}
          isOptional={true}
        />
      </div>
    </SpeciesDetailsContainer>
  )
}

RegionalNationalEstimatesDataContent.propTypes = {
  parameter: PropTypes.object.isRequired,
  siteSpecieParameterId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  updateParameterList: PropTypes.func.isRequired,
  species: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    historyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
  }).isRequired,
  qualified: PropTypes.bool.isRequired
}

export default RegionalNationalEstimatesDataContent
